import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquare, Send } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { useState } from "react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";

export default function Feedback() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: user?.email || "",
    category: "",
    message: ""
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from("feedback" as any)
        .insert({
          user_id: user?.id,
          name: formData.name,
          email: formData.email,
          category: formData.category,
          message: formData.message,
        });

      if (error) throw error;

      toast({
        title: t("feedback.success"),
        description: t("feedback.successDesc"),
      });

      // Reset form
      setFormData({
        name: "",
        email: user?.email || "",
        category: "",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast({
        title: t("feedback.error"),
        description: t("feedback.errorDesc"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="marketing-page min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <Navigation />
      <div className="container mx-auto px-4 py-8 pt-24">
        <div className="max-w-3xl mx-auto space-y-8">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center mb-4">
              <MessageSquare className="h-12 w-12 text-primary mr-3" />
              <h1 className="text-4xl font-bold">{t("feedback.title")}</h1>
            </div>
            <p className="text-xl text-muted-foreground">
              {t("feedback.subtitle")}
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{t("feedback.formTitle")}</CardTitle>
              <CardDescription>
                {t("feedback.formDesc")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">{t("feedback.yourName")} *</Label>
                  <Input
                    id="name"
                    placeholder={t("feedback.yourName")}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    maxLength={100}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t("feedback.yourEmail")} *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t("feedback.emailPlaceholder")}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    maxLength={255}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">{t("feedback.category")} *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                    required
                  >
                    <SelectTrigger id="category">
                      <SelectValue placeholder={t("feedback.category")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bug">{t("feedback.categories.bug")}</SelectItem>
                      <SelectItem value="feature">{t("feedback.categories.feature")}</SelectItem>
                      <SelectItem value="improvement">{t("feedback.categories.improvement")}</SelectItem>
                      <SelectItem value="ui">{t("feedback.categories.ui")}</SelectItem>
                      <SelectItem value="performance">{t("feedback.categories.performance")}</SelectItem>
                      <SelectItem value="other">{t("feedback.categories.general")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">{t("feedback.yourFeedback")} *</Label>
                  <Textarea
                    id="message"
                    placeholder={t("feedback.yourFeedback")}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={6}
                    maxLength={2000}
                    className="max-h-[400px]"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    {formData.message.length}/2000
                  </p>
                </div>

                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  <Send className="mr-2 h-4 w-4" />
                  {isSubmitting ? t("feedback.submitting") : t("feedback.submit")}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="bg-primary/5 border-primary/20">
            <CardHeader>
              <CardTitle className="text-lg">{t("feedback.whyMatters")}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{t("feedback.whyMattersText")}</p>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
}


